import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { format } from "date-fns";
import { ArrowRight } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDeliveryQuote } from "@/lib/useDeliveryQuote";
import { ServicePicker } from "@/components/booking/ServicePicker";
import { DetailsForm } from "@/components/booking/DetailsForm";
import { DeliverySummary } from "@/components/booking/DeliverySummary";
import { MobileCheckoutBar } from "@/components/booking/MobileCheckoutBar";

const EMPTY = { customer_name: "", customer_email: "", customer_phone: "", address: "", pickup_date: "", notes: "" };

export default function Booking() {
  const { user } = useAuth();
  const repeat = useLocation().state?.repeat;
  const [services, setServices] = useState([]);
  const [config, setConfig] = useState(null);
  const [qty, setQty] = useState({});
  const [step, setStep] = useState(1);
  const [paying, setPaying] = useState(false);
  const [pickupDate, setPickupDate] = useState(null);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [details, setDetails] = useState(EMPTY);
  const { quote, loading: quoteLoading, error: quoteError } = useDeliveryQuote(step === 2 ? details.address : "");

  useEffect(() => {
    api.get("/services").then(({ data }) => setServices(data)).catch(() => {});
    api.get("/delivery/config").then(({ data }) => setConfig(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) return;
    setDetails((d) => ({
      ...d,
      customer_name: d.customer_name || user.name || "",
      customer_email: d.customer_email || user.email || "",
      customer_phone: d.customer_phone || user.phone || "",
      address: d.address || user.address || "",
    }));
  }, [user]);

  useEffect(() => {
    if (!repeat || services.length === 0) return;
    const q = {};
    repeat.items.forEach((i) => {
      if (services.some((s) => s.id === i.service_id)) q[i.service_id] = Math.min(50, i.quantity);
    });
    window.history.replaceState({}, "");
    if (Object.keys(q).length === 0) {
      toast.error("Los servicios de ese pedido ya no están disponibles");
      return;
    }
    setQty(q);
    setDetails((d) => ({ ...d, address: repeat.address || d.address, notes: repeat.notes || d.notes }));
    setStep(2);
    toast.success("Pedido anterior cargado: elige la fecha de recogida y paga");
  }, [repeat, services]);

  const items = services.filter((s) => (qty[s.id] || 0) > 0);
  const subtotal = items.reduce((acc, s) => acc + s.price * qty[s.id], 0);
  const deliveryFee = quote?.fee || 0;
  const total = subtotal + deliveryFee;

  const changeQty = (id, delta) =>
    setQty((q) => ({ ...q, [id]: Math.max(0, Math.min(50, (q[id] || 0) + delta)) }));

  const goToStep2 = () => {
    if (items.length === 0) {
      toast.error("Selecciona al menos un servicio");
      return;
    }
    setStep(2);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const pay = async (e) => {
    e.preventDefault();
    if (!pickupDate) {
      toast.error("Selecciona una fecha de recogida");
      return;
    }
    if (!acceptTerms) {
      toast.error("Debes aceptar las Condiciones del servicio y la Política de privacidad");
      return;
    }
    setPaying(true);
    try {
      const { data } = await api.post("/orders/checkout", {
        ...details,
        pickup_date: format(pickupDate, "yyyy-MM-dd"),
        items: items.map((s) => ({ service_id: s.id, quantity: qty[s.id] })),
        origin_url: window.location.origin,
        accept_terms: true,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setPaying(false);
    }
  };

  return (
    <div className="bg-slate-50 min-h-screen" data-testid="booking-page">
      <Navbar />
      <main className="pt-24 sm:pt-28 pb-12 lg:pb-20 max-w-7xl mx-auto px-4 sm:px-6">
        <h1 className="text-3xl sm:text-4xl font-heading font-bold text-slate-900 mb-2">Reservar Recogida</h1>
        <p className="text-slate-600 mb-6 sm:mb-10">
          {step === 1 ? "Paso 1 de 2 — Elige tus servicios" : "Paso 2 de 2 — Datos de recogida y pago"}
        </p>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-10">
          <div className="lg:col-span-2">
            {step === 1 ? (
              <ServicePicker services={services} qty={qty} onChange={changeQty} config={config} />
            ) : (
              <DetailsForm
                details={details} setDetails={setDetails}
                pickupDate={pickupDate} setPickupDate={setPickupDate}
                acceptTerms={acceptTerms} setAcceptTerms={setAcceptTerms}
                onSubmit={pay} onBack={() => setStep(1)} paying={paying}
                payDisabled={quoteLoading || Boolean(quoteError)} total={total} user={user}
              />
            )}
          </div>
          <aside className="lg:sticky lg:top-28 h-fit bg-white border border-slate-200 rounded-2xl p-5 sm:p-6" data-testid="booking-summary">
            <h2 className="font-heading font-semibold text-lg text-slate-900 mb-4">Resumen del pedido</h2>
            {items.length === 0 ? (
              <p className="text-sm text-slate-500">Aún no has seleccionado servicios.</p>
            ) : (
              <ul className="space-y-3 text-sm">
                {items.map((s) => (
                  <li key={s.id} className="flex justify-between">
                    <span className="text-slate-600">{s.name} x{qty[s.id]}</span>
                    <span className="font-medium">{(s.price * qty[s.id]).toFixed(2)} €</span>
                  </li>
                ))}
                <DeliverySummary quote={quote} loading={quoteLoading} error={quoteError} config={config} />
              </ul>
            )}
            <div className="border-t border-slate-200 mt-4 pt-4 flex justify-between font-heading font-bold text-lg">
              <span>Total</span>
              <span className="text-cyan-600" data-testid="booking-total">{total.toFixed(2)} €</span>
            </div>
            <p className="text-xs text-slate-400 mt-2">Los impuestos se calculan en el pago.</p>
            {step === 1 && (
              <Button onClick={goToStep2} data-testid="booking-continue-btn"
                className="hidden lg:inline-flex w-full mt-5 bg-cyan-500 hover:bg-cyan-600 rounded-full h-11">
                Continuar <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            )}
          </aside>
        </div>
      </main>
      <MobileCheckoutBar
        step={step} itemsCount={items.length} total={total} quote={quote}
        onContinue={goToStep2} paying={paying} payDisabled={quoteLoading || Boolean(quoteError)}
      />
      <Footer />
    </div>
  );
}
